import { useEffect, useRef, useState } from 'react';
import { PERMISSION, can, useAuth, type Agent, type Conversation } from '@wa/shared';

/**
 * The chat header's action row.
 *
 * Icon-only, in the order the design being matched uses: call, assignee, mark as
 * done, search, kebab, and the details toggle. Every one carries a tooltip and an
 * accessible name, because a row of six unlabelled glyphs is a memory test.
 */
const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

export function CallButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton label="Call" onClick={() => setOpen(true)}>
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <path d="M5.4 3h2.5l1.2 3.1-1.7 1.2a10 10 0 0 0 5.3 5.3l1.2-1.7L17 12.1v2.5a1.7 1.7 0 0 1-1.9 1.7A13.8 13.8 0 0 1 3.7 4.9 1.7 1.7 0 0 1 5.4 3z" {...stroke} />
        </svg>
      </IconButton>

      {open && (
        <Explainer
          title="Calling is not switched on"
          onClose={() => setOpen(false)}
          body="WhatsApp calling needs this number to hold a 2,000-a-day messaging limit. Ours is below that, so Meta will not enable it yet. The limit rises on its own as delivery quality holds."
        />
      )}
    </>
  );
}

/**
 * The assignee pill.
 *
 * A pill rather than an icon, because who owns a conversation is the one piece of
 * state in this header that has a value worth reading at a glance — and it is the
 * shape the design being matched uses.
 */
export function AssigneePill({ conversation, agents, isSaving, onAssign }: {
  conversation: Conversation;
  agents: Agent[];
  isSaving: boolean;
  onAssign: (agentId: number | null) => void;
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const mayAssign = can(user, PERMISSION.inboxAssign);
  const name = conversation.assignedAgentName;

  const shown = agents.filter((a) =>
    a.displayName.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div ref={root} className="relative shrink-0">
      <button
        type="button"
        onClick={() => mayAssign && setOpen((v) => !v)}
        disabled={!mayAssign}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name ? `Assigned to ${name}` : 'Unassigned'}
        aria-label={name ? `Assigned to ${name}. Change.` : 'Unassigned. Assign somebody.'}
        className="tap flex h-8 items-center gap-1.5 rounded-(--radius-field) border border-(--color-line) px-2 transition-colors hover:bg-(--color-sunken) disabled:opacity-60"
      >
        {name ? (
          <span className="grid size-5 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-micro font-semibold text-(--color-brand)">
            {name.trim().charAt(0).toUpperCase()}
          </span>
        ) : (
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" className="text-(--color-muted)">
            <circle cx="10" cy="7.4" r="2.6" {...stroke} />
            <path d="M4.8 16a5.2 5.2 0 0 1 10.4 0" {...stroke} />
          </svg>
        )}
        <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true"
             className={['text-(--color-muted) transition-transform duration-150 motion-reduce:transition-none',
                         open ? 'rotate-180' : ''].join(' ')}>
          <path d="M5.5 8 10 12.5 14.5 8" {...stroke} />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 flex max-h-96 w-64 flex-col overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)"
        >
          <div className="shrink-0 border-b border-(--color-line) p-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              aria-label="Search people"
              className="h-9 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-2.5 text-small outline-none focus:border-(--color-brand) focus:bg-(--color-surface)"
            />
          </div>

          {/* No "AI Agents" tab. The design being matched has one and we have no AI
              agents — a tab that opens an empty list is a promise the product does
              not keep at the moment somebody clicks it. */}
          <p className="shrink-0 border-b border-(--color-line) px-3 py-2 text-micro font-medium tracking-wide text-(--color-muted) uppercase">
            Agents <span className="tabular-nums">{agents.length}</span>
          </p>

          <div className="pane-scroll min-h-0 flex-1 p-1">
            <button
              type="button"
              role="menuitem"
              disabled={isSaving || conversation.assignedAgentId === null}
              onClick={() => { setOpen(false); onAssign(null); }}
              className="tap flex w-full items-center gap-2.5 rounded-(--radius-field) px-2 py-2 text-left text-small text-(--color-ink-soft) hover:bg-(--color-sunken) disabled:opacity-40"
            >
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" className="shrink-0 text-(--color-muted)">
                <circle cx="10" cy="7.4" r="2.6" {...stroke} />
                <path d="M4.8 16a5.2 5.2 0 0 1 10.4 0M3.6 3.6 16.4 16.4" {...stroke} />
              </svg>
              Unassign chat
            </button>

            {shown.map((agent) => {
              const current = agent.id === conversation.assignedAgentId;
              return (
                <button
                  key={agent.id}
                  type="button"
                  role="menuitem"
                  disabled={isSaving}
                  onClick={() => { setOpen(false); onAssign(agent.id); }}
                  className={[
                    'tap flex w-full items-center gap-2.5 rounded-(--radius-field) px-2 py-2 text-left text-small transition-colors disabled:opacity-40',
                    current ? 'bg-(--color-brand-wash) text-(--color-brand)' : 'hover:bg-(--color-sunken)',
                  ].join(' ')}
                >
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-(--color-sunken) text-micro font-semibold">
                    {agent.displayName.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{agent.displayName}</span>
                  {current && (
                    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" className="shrink-0">
                      <path d="m4.5 10.4 3.4 3.3 7.6-7.4" {...stroke} strokeWidth="1.9" />
                    </svg>
                  )}
                </button>
              );
            })}

            {shown.length === 0 && (
              <p className="px-2 py-3 text-small text-(--color-muted)">Nobody by that name.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Mark as done. Ours calls it resolving; theirs is the same act with a tick. */
export function DoneButton({ done, disabled, onClick }: {
  done: boolean; disabled: boolean; onClick: () => void;
}) {
  return (
    <IconButton
      label={done ? 'Already marked as done' : 'Mark as done'}
      disabled={disabled || done}
      active={done}
      onClick={onClick}
    >
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
        <circle cx="10" cy="10" r="7.2" {...stroke} />
        <path d="m6.7 10.2 2.3 2.2 4.4-4.5" {...stroke} />
      </svg>
    </IconButton>
  );
}

/** The details column's own toggle, so a wide screen can give the thread the room. */
export function DetailsToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <IconButton label={open ? 'Hide details' : 'Show details'} onClick={onClick}>
      {/* Points right while the panel is open — "push it away" — and left while it
          is shut. It was the other way round, which reads as "go back". */}
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"
           className={['transition-transform duration-200 ease-out motion-reduce:transition-none',
                       open ? '' : 'rotate-180'].join(' ')}>
        <path d="M7.5 4 13.5 10l-6 6" {...stroke} />
      </svg>
    </IconButton>
  );
}

export function IconButton({ label, onClick, disabled = false, active = false, children }: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        'tap grid size-8 shrink-0 place-items-center rounded-(--radius-field) transition-colors',
        active
          ? 'bg-(--color-brand-wash) text-(--color-brand)'
          : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
        'disabled:opacity-40 disabled:hover:bg-transparent',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/**
 * A control that leads somewhere explaining itself, rather than a dead one.
 *
 * The build reference has said since M1 that every dead entry is a promise the
 * product does not keep at the moment somebody clicks it. A gated control that says
 * what it is waiting for is not that.
 */
function Explainer({ title, body, onClose }: { title: string; body: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xs rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-4 text-center shadow-(--shadow-pop)"
      >
        <h2 className="text-lead font-semibold tracking-tight">{title}</h2>
        <p className="mt-2 text-small text-(--color-muted)">{body}</p>
        <button
          type="button"
          onClick={onClose}
          className="tap mt-4 w-full rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-body font-medium hover:bg-(--color-sunken)"
        >
          Close
        </button>
      </div>
    </div>
  );
}
